import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAIVoiceAgentsDashboard } from '@/lib/hooks';
import {
  Bot,
  Phone,
  FileText,
  Settings,
  Activity,
  Users,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { ThemedCard } from '@/components/ui/ThemedCard';
import { supabase } from '@/lib/supabase';

export default function AIVoiceAgentsOverview() {
  const { theme } = useTheme();
  const {
    activeCompaniesCount,
    totalDocuments,
    totalRules,
    stats,
    recentCalls,
    isLoading: loading,
    error
  } = useAIVoiceAgentsDashboard();

  // ElevenLabs health status (server-side aggregator)
  const [health, setHealth] = useState<any>(null);
  const [healthLoading, setHealthLoading] = useState<boolean>(true);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('elevenlabs-health', { body: {} });
        if (!mounted) return;
        if (error) {
          console.error('Health fetch error:', error);
          setHealth(null);
        } else {
          setHealth(data);
        }
      } catch (e) {
        console.warn('Health fetch failed:', e);
        if (mounted) setHealth(null);
      } finally {
        if (mounted) setHealthLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (error) {
    console.error('Error loading dashboard data:', error);
  }

  function getStatusIcon(status: string) {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" style={{ color: theme.colors.successText }} />;
      case 'transferred':
        return <Users className="w-4 h-4" style={{ color: theme.colors.infoText }} />;
      case 'failed':
        return <AlertCircle className="w-4 h-4" style={{ color: theme.colors.dangerText }} />;
      default:
        return <Clock className="w-4 h-4" style={{ color: theme.colors.textTertiary }} />;
    }
  }

  function getStatusStyle(status: string) {
    switch (status) {
      case 'completed':
        return { backgroundColor: theme.colors.successBg, color: theme.colors.successText };
      case 'transferred':
        return { backgroundColor: theme.colors.infoBg, color: theme.colors.infoText };
      case 'failed':
        return { backgroundColor: theme.colors.dangerBg, color: theme.colors.dangerText };
      default:
        return { backgroundColor: theme.colors.bgHover, color: theme.colors.textSecondary };
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8" style={{ borderWidth: '2px', borderColor: theme.colors.borderColor, borderTopColor: theme.colors.primaryColor }}></div>
      </div>
    );
  }

  return (
    <div className="p-6" style={{ backgroundColor: theme.colors.bgPage }}>
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: theme.colors.textPrimary }}>AI Voice Agents</h1>
        <p className="mt-2" style={{ color: theme.colors.textSecondary }}>
          Manage your ElevenLabs-powered voice agents, knowledge bases, and call routing
        </p>
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>📊 Data Source:</strong> All metrics below show data from AI-handled calls only (from the <code>ai_calls</code> table).
            These are calls that were processed by your ElevenLabs voice agents, not general call center calls.
          </p>
        </div>
      </div>

      {/* About Banner */}
      <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: theme.colors.infoBg, borderColor: theme.colors.infoBorder }}>
        <h3 className="font-semibold" style={{ color: theme.colors.infoText }}>About this page</h3>
        <ul className="mt-2 text-sm list-disc pl-5 space-y-1" style={{ color: theme.colors.infoText }}>
          <li>Purpose: overview of AI Voice Agents setup, health, and performance. Use Quick Actions to jump into configuration.</li>
          <li>Data sources: metrics are derived from the <code className="font-mono">ai_calls</code> table and aggregate across active companies.</li>
          <li>Getting started: 1) Companies → 2) Knowledge Base (docs + FAQs) → 3) Agent Rules + Hard Rules → 4) Phone Numbers → 5) Monitor Performance.</li>
          <li>Seeding: initial scripts/knowledge can be seeded from <code className="font-mono">inbound.md</code> and <code className="font-mono">outbound.md</code> via <code className="font-mono">scripts/seed-ai-knowledge.mjs</code>.</li>
          <li>Duplication policy: prefer unified <code className="font-mono">Rules</code> over legacy Agent Rules; prefer <code className="font-mono">Knowledge Base</code> over legacy Agent Knowledge; store FAQs as <code className="font-mono">knowledge_articles</code> with <code className="font-mono">article_type="faq"</code>.</li>
        </ul>
      </div>

      {/* Health Status */}
      <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: theme.colors.bgCard, borderColor: theme.colors.borderColor }}>
        <h3 className="font-semibold mb-2" style={{ color: theme.colors.textPrimary }}>ElevenLabs Integration Health</h3>
        {healthLoading ? (
          <p className="text-sm" style={{ color: theme.colors.textSecondary }}>Checking health...</p>
        ) : (
          <ul className="mt-1 text-sm list-disc pl-5 space-y-1" style={{ color: theme.colors.textSecondary }}>
            <li>
              ConvAI write permission:{" "}
              <span style={{ color: health?.convai_write ? theme.colors.successText : theme.colors.dangerText }}>
                {health?.convai_write ? 'OK' : 'Missing'}
              </span>
            </li>
            <li>
              Gateway function:{" "}
              <span style={{ color: health?.gateway?.ok ? theme.colors.successText : theme.colors.dangerText }}>
                {health?.gateway?.ok ? 'OK' : `Unavailable (status ${health?.gateway?.status ?? 'n/a'})`}
              </span>
            </li>
            <li>
              API key present:{" "}
              <span style={{ color: health?.xi_key_present ? theme.colors.successText : theme.colors.dangerText }}>
                {health?.xi_key_present ? 'Yes' : 'No'}
              </span>
            </li>
            <li>
              Default agent id present:{" "}
              <span style={{ color: health?.default_agent_id_present ? theme.colors.successText : theme.colors.dangerText }}>
                {health?.default_agent_id_present ? 'Yes' : 'No'}
              </span>
            </li>
          </ul>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <ThemedCard>
          <div className="flex items-center">
            <div className="p-2 rounded-lg" style={{ backgroundColor: theme.colors.primaryColor + '20' }}>
              <Bot className="w-6 h-6" style={{ color: theme.colors.primaryColor }} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>Total Companies</p>
              <p className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>{activeCompaniesCount}</p>
            </div>
          </div>
        </ThemedCard>

        <ThemedCard>
          <div className="flex items-center">
            <div className="p-2 rounded-lg" style={{ backgroundColor: theme.colors.successBg }}>
              <Phone className="w-6 h-6" style={{ color: theme.colors.successText }} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>Total Calls</p>
              <p className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>{stats.totalCalls}</p>
            </div>
          </div>
        </ThemedCard>

        <ThemedCard>
          <div className="flex items-center">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#a855f720' }}>
              <FileText className="w-6 h-6" style={{ color: '#a855f7' }} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>Knowledge Base</p>
              <p className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>{totalDocuments}</p>
            </div>
          </div>
        </ThemedCard>

        <ThemedCard>
          <div className="flex items-center">
            <div className="p-2 rounded-lg" style={{ backgroundColor: theme.colors.warningBg }}>
              <Settings className="w-6 h-6" style={{ color: theme.colors.warningText }} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>Active Rules</p>
              <p className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>{totalRules}</p>
            </div>
          </div>
        </ThemedCard>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <ThemedCard>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>Active Calls</p>
              <p className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>{stats.activeCalls}</p>
            </div>
            <Activity className="w-8 h-8" style={{ color: theme.colors.successText }} />
          </div>
        </ThemedCard>

        <ThemedCard>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>Avg Duration</p>
              <p className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>{stats.avgCallDuration}s</p>
            </div>
            <Clock className="w-8 h-8" style={{ color: theme.colors.primaryColor }} />
          </div>
        </ThemedCard>

        <ThemedCard>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>Transfer Rate</p>
              <p className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>{stats.transferRate}%</p>
            </div>
            <TrendingUp className="w-8 h-8" style={{ color: '#a855f7' }} />
          </div>
        </ThemedCard>
      </div>

      {/* Quick Actions */}
      <ThemedCard padding="p-6" className="mb-8">
        <h2 className="text-lg font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/ai-voice-agents/companies"
            className="flex items-center p-4 border rounded-lg transition-colors"
            style={{ borderColor: theme.colors.borderColor }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <Bot className="w-8 h-8 mr-3" style={{ color: theme.colors.primaryColor }} />
            <div>
              <h3 className="font-medium" style={{ color: theme.colors.textPrimary }}>Manage Companies</h3>
              <p className="text-sm" style={{ color: theme.colors.textSecondary }}>Configure company settings and voice agents</p>
            </div>
          </Link>

          <Link
            to="/ai-voice-agents/knowledge-base"
            className="flex items-center p-4 border rounded-lg transition-colors"
            style={{ borderColor: theme.colors.borderColor }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <FileText className="w-8 h-8 mr-3" style={{ color: theme.colors.successText }} />
            <div>
              <h3 className="font-medium" style={{ color: theme.colors.textPrimary }}>Knowledge Base</h3>
              <p className="text-sm" style={{ color: theme.colors.textSecondary }}>Add documents and FAQs for AI agents</p>
            </div>
          </Link>

          <Link
            to="/ai-voice-agents/rules"
            className="flex items-center p-4 border rounded-lg transition-colors"
            style={{ borderColor: theme.colors.borderColor }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <Settings className="w-8 h-8 mr-3" style={{ color: '#a855f7' }} />
            <div>
              <h3 className="font-medium" style={{ color: theme.colors.textPrimary }}>Agent Rules</h3>
              <p className="text-sm" style={{ color: theme.colors.textSecondary }}>Configure obligations and escalation rules</p>
            </div>
          </Link>
        </div>
      </ThemedCard>

      {/* Recent Calls */}
      <ThemedCard padding="p-6">
        <h2 className="text-lg font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>Recent Calls</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y" style={{ borderColor: theme.colors.borderColor }}>
            <thead style={{ backgroundColor: theme.colors.bgHover }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                  From
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                  To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                  Started
                </th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: theme.colors.borderColor }}>
              {recentCalls.map((call: any) => (
                <tr 
                  key={call.id} 
                  className="transition-colors"
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getStatusIcon(call.status)}
                      <span className="ml-2 px-2 py-1 text-xs rounded-full" style={getStatusStyle(call.status)}>
                        {call.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: theme.colors.textPrimary }}>
                    {call.from_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: theme.colors.textPrimary }}>
                    {call.to_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: theme.colors.textPrimary }}>
                    {call.company_configs?.name || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: theme.colors.textPrimary }}>
                    {call.duration_seconds ? `${call.duration_seconds}s` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: theme.colors.textSecondary }}>
                    {new Date(call.started_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {recentCalls.length === 0 && (
          <div className="text-center py-8">
            <Phone className="w-12 h-12 mx-auto mb-4" style={{ color: theme.colors.textTertiary }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: theme.colors.textPrimary }}>No calls yet</h3>
            <p style={{ color: theme.colors.textSecondary }}>AI voice agent calls will appear here once they start coming in.</p>
          </div>
        )}
      </ThemedCard>
    </div>
  );
}
